import type { Rule } from 'graph-grammar'
import { GripVertical, Copy, X, Lock } from 'lucide-react'
import { useApp } from '../../AppContext.tsx'
import { labelColor } from '../../colors.ts'
import { rule as makeRule } from 'graph-grammar'

function Chip({ children }: { children: React.ReactNode }) {
  return <span className="meta-chip">{children}</span>
}

/** Drag-to-reorder wiring, supplied by the parent list and bound to this row. */
export interface RuleDnd {
  dragging: boolean
  over: boolean
  onStart: () => void
  onOver: () => void
  onLeave: () => void
  onDrop: () => void
  onEnd: () => void
}

export function RuleRow({
  rule,
  count,
  cap,
  active,
  blocked,
  dnd,
}: {
  rule: Rule
  count: number
  cap: number
  active: boolean
  blocked?: boolean
  dnd?: RuleDnd
}) {
  const app = useApp()
  const badge = count >= cap ? `${cap}+` : String(count)

  const duplicate = () => {
    const copy = structuredClone(rule)
    copy.id = makeRule({ name: '', lhs: { nodes: [], edges: [] }, rhs: { nodes: [], edges: [] } }).id
    copy.name = rule.name + ' (copy)'
    app.addRule(copy)
  }

  return (
    <div
      className={
        'rule-row' + (active ? ' active' : '') + (dnd?.dragging ? ' dragging' : '') + (dnd?.over ? ' drag-over' : '')
      }
      onClick={() => app.selectRule(rule.id)}
      draggable={!!dnd}
      onDragStart={
        dnd
          ? (e) => {
              dnd.onStart()
              e.dataTransfer.effectAllowed = 'move'
              e.dataTransfer.setData('text/plain', rule.id)
            }
          : undefined
      }
      onDragOver={
        dnd
          ? (e) => {
              e.preventDefault()
              e.dataTransfer.dropEffect = 'move'
              dnd.onOver()
            }
          : undefined
      }
      onDragLeave={dnd ? () => dnd.onLeave() : undefined}
      onDrop={
        dnd
          ? (e) => {
              e.preventDefault()
              dnd.onDrop()
            }
          : undefined
      }
      onDragEnd={dnd ? () => dnd.onEnd() : undefined}
    >
      <div className="rule-row-top">
        <input
          type="checkbox"
          checked={rule.enabled}
          title="Enable/disable in runs"
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            rule.enabled = e.target.checked
            app.touchRules()
          }}
        />
        <span
          className={'match-badge' + (blocked ? ' blocked' : count > 0 ? ' has' : '')}
          title={blocked ? `${count} matches, but blocked , firing would exceed Max nodes` : `${count} current matches`}
        >
          {blocked ? (
            <>
              {badge} <Lock size={11} />
            </>
          ) : (
            badge
          )}
        </span>
        <div className="rule-main">
          <div className="rule-name-row">
            <span className="rule-swatch" style={{ background: rule.color || labelColor(rule.name) }} />
            <span className="rule-name" title={rule.description || rule.name}>
              {rule.name}
            </span>
          </div>
          <div className="rule-meta">
            <span className="meta-chip" title="LHS → RHS (nodes/edges)">
              {rule.lhs.nodes.length}n/{rule.lhs.edges.length}e → {rule.rhs.nodes.length}n/{rule.rhs.edges.length}e
            </span>
            {rule.probability < 1 && <Chip>p={rule.probability}</Chip>}
            {rule.weight !== 1 && <Chip>w={rule.weight}</Chip>}
            {rule.priority !== 0 && <Chip>pri {rule.priority}</Chip>}
          </div>
        </div>
      </div>
      <div className="rule-controls">
        <span className="drag-handle" title="Drag to reorder" aria-hidden>
          <GripVertical size={15} />
        </span>
        <button
          className="icon-btn"
          title="Duplicate"
          onClick={(e) => {
            e.stopPropagation()
            duplicate()
          }}
        >
          <Copy size={14} />
        </button>
        <button
          className="icon-btn"
          title="Delete"
          onClick={(e) => {
            e.stopPropagation()
            if (confirm(`Delete rule “${rule.name}”?`)) app.removeRule(rule.id)
          }}
        >
          <X size={15} />
        </button>
      </div>
    </div>
  )
}
